import { createEffect, createEvent, sample } from 'effector';
import { getWebnotesMenuTree } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import {
  $menuState,
  localStorageExpandedItemsKey,
  $menuExpandedItemIds,
  menuItemTypeLocMap,
  tagToMenuItemDto,
} from '@nx-frontend/shared/store/feature-menu-tree';
import { $notes, NotesState, SortOrder, fetchNotesFx, getNotes } from '@nx-frontend/shared/store/notes';
import {
  MenuItemType,
  PortalServiceMenuItemDto,
  MenuType,
  NoteMenuItem,
  NoteDto,
  MenuItem,
  FetchSidebarMenuTreeParams,
  TranslatesFnType,
  Tag,
} from '@nx-frontend/shared/types';
import { Workspace } from '@nx-frontend/shared/types';
import { GetNotesResult, NoteType } from '@nx-frontend/shared/types';
import { MenuState, MenusState } from '@nx-frontend/shared/types';
import { isValidUrl } from '@nx-frontend/shared/utils';
import { scrollToSidebarMenuItem } from '@nx-frontend/shared/utils';
import { getSortFn } from '@nx-frontend/shared/utils';

const sortBy: keyof MenuItem = 'name';
const sortOrder: SortOrder = 'ASC';

type MenuItemWithOptionalDate = PortalServiceMenuItemDto & {
  createdAt?: number;
  updatedAt?: number;
  isShared?: boolean;
};

const sortChildren = (children: MenuItemWithOptionalDate[]) => {
  children.sort(getSortFn<MenuItemWithOptionalDate>(sortBy, sortOrder)).forEach((item, index) => (item.index = index));
  children.forEach((item) => {
    if (item.children) {
      sortChildren(item.children);
    }
  });
};

export const sidebarMenuTreeDefaultItems: MenuItemWithOptionalDate[] = [
  {
    positionType: 'sidebar',
    type: MenuItemType.ALL_PAGES,
    id: `${MenuItemType.ALL_PAGES}#${MenuItemType.ALL_PAGES}`,
    parentId: '',
    hasChildren: false,
    name: 'All pages',
    index: -106,
    icon: 'page',
    visible: true,
    openInNewTab: false,
    url: '',
    slug: '',
  },
  {
    positionType: 'sidebar',
    type: MenuItemType.FILES_MANAGER,
    id: `${MenuItemType.FILES_MANAGER}#${MenuItemType.FILES_MANAGER}`,
    parentId: '',
    hasChildren: false,
    name: 'Files',
    index: -105,
    icon: 'paperclip',
    visible: true,
    openInNewTab: false,
    url: '',
    slug: '',
  },
  {
    positionType: 'sidebar',
    type: MenuItemType.TASKS,
    id: `${MenuItemType.TASKS}#${MenuItemType.TASKS}`,
    parentId: '',
    hasChildren: false,
    name: 'Tasks',
    index: -104,
    icon: 'tasks',
    visible: true,
    openInNewTab: false,
    url: '',
    slug: '',
  },
  {
    positionType: 'sidebar',
    type: MenuItemType.AI_AGENTS,
    id: `${MenuItemType.AI_AGENTS}#${MenuItemType.AI_AGENTS}`,
    parentId: '',
    hasChildren: false,
    name: 'AI Agents',
    index: -103,
    icon: 'ai_agents',
    visible: true,
    openInNewTab: false,
    url: '',
    slug: '',
  },
  {
    positionType: 'sidebar',
    type: MenuItemType.TAGS,
    id: `${MenuItemType.TAGS}#root`,
    parentId: '',
    hasChildren: true,
    name: 'Tags',
    index: -102,
    icon: 'tag',
    visible: true,
    openInNewTab: false,
    url: '',
    slug: '',
  },
  {
    positionType: 'sidebar',
    type: MenuItemType.CHAT,
    id: `${MenuItemType.CHAT}#${MenuItemType.CHAT}`,
    parentId: '',
    hasChildren: false,
    name: 'Chat',
    index: -101,
    icon: 'chat',
    visible: true,
    openInNewTab: false,
    url: '',
    slug: '',
  },
  {
    positionType: 'sidebar',
    type: MenuItemType.FOLDERS,
    id: `${MenuItemType.NOTES_FOLDER}#root`,
    parentId: '',
    hasChildren: true,
    children: [],
    name: 'Folders',
    index: -100,
    icon: 'folders',
    visible: true,
    openInNewTab: false,
    url: '',
    slug: '',
  },
];

export const getSidebarMenuTreeDefaultItems = (flags?: { disableTags?: boolean; disableAIAgents?: boolean }) => {
  let items = sidebarMenuTreeDefaultItems;
  if (flags?.disableTags) {
    items = items.filter((item) => item.type !== MenuItemType.TAGS);
  }
  if (flags?.disableAIAgents) {
    items = items.filter((item) => item.type !== MenuItemType.AI_AGENTS);
  }

  return items;
};

// HACK: since sidebar preparation functions work with Dtos before converting them to MenuItems,
// we need to covnert tags to Dto first
export const createTagsMenuItemDtos = (tags: Tag[]): MenuItemWithOptionalDate[] => {
  return tags.map((tag) => tagToMenuItemDto(tag));
};

export const prepareRootItems = (items: MenuItemWithOptionalDate[]): MenuItemWithOptionalDate[] => {
  return items.sort(getSortFn<MenuItemWithOptionalDate>(sortBy, sortOrder)).map((item, index) => {
    return { ...item, index, parentId: `${item.type === 'tag' ? MenuItemType.TAGS : MenuItemType.NOTES_FOLDER}#root` };
  });
};

interface GetSidebarMenuTreeArguments {
  fetchedItems: MenuItemWithOptionalDate[];
  tagsItems?: MenuItemWithOptionalDate[];
  t?: TranslatesFnType;
  showAIAgents?: boolean;
}

export const getSidebarMenuTree = ({
  fetchedItems,
  tagsItems,
  t,
  showAIAgents,
}: GetSidebarMenuTreeArguments): MenuItemWithOptionalDate[] => {
  let sidebarMenuTree: MenuItemWithOptionalDate[] = getSidebarMenuTreeDefaultItems({
    disableTags: !tagsItems,
    disableAIAgents: !showAIAgents,
  });
  if (t) {
    sidebarMenuTree = sidebarMenuTree.map((item) => {
      const locKey = menuItemTypeLocMap[item.type];
      const localizedName = locKey && t(locKey);
      return { ...item, name: localizedName || item.name || '' };
    });
  }
  // Insert folders and notes into "Folders" group
  const foldersIndex = sidebarMenuTree.findIndex((item) => item.type === MenuItemType.FOLDERS);
  const foldersChildren = prepareRootItems(fetchedItems);
  sortChildren(foldersChildren);
  sidebarMenuTree.splice(foldersIndex, 1, {
    ...sidebarMenuTree[foldersIndex],
    children: foldersChildren,
  });

  if (tagsItems) {
    // Insert tags into "Tags" group
    const tagsMenuItems = tagsItems ?? [];
    const tagsIndex = sidebarMenuTree.findIndex((item) => item.type === MenuItemType.TAGS);
    const tagsChildren = prepareRootItems(tagsMenuItems);
    sortChildren(tagsChildren);
    sidebarMenuTree.splice(tagsIndex, 1, {
      ...sidebarMenuTree[tagsIndex],
      children: tagsChildren,
    });
  }

  return sidebarMenuTree;
};

/**
 * Effect fetch notes only from folders in parentKey
 * In parentKey can set sigle id or ids of folders separated by comma `id1,id2,id3`
 */
export const fetchItemsInOpenendFoldersFx = createEffect(
  async (params: FetchSidebarMenuTreeParams): Promise<{ notes: MenuItemWithOptionalDate[]; force: boolean }> => {
    const { workspaceId, parentKey, abortController, force = false } = params;
    const notes = await getWebnotesMenuTree({
      gatewayUrl: generalEnv.gatewayUrl,
      workspaceId,
      menuType: MenuType.WEBNOTES_MENU,
      parentKey,
      type: 'note',
      depth: -1,
      abortController,
      orderBy: 'title',
      orderDirection: 'ASC',
    });
    return { notes, force };
  }
);

interface AfterFetchItemsInOpenendFoldersEventPayload {
  menuExpandedItemIds: string[];
  notes: MenuItemWithOptionalDate[];
  force: boolean;
}

const afterFetchItemsInOpenendFoldersEvent = createEvent<AfterFetchItemsInOpenendFoldersEventPayload>();

sample({
  source: {
    menuExpandedItemIds: $menuExpandedItemIds,
  },
  clock: fetchItemsInOpenendFoldersFx.doneData,
  fn: ({ menuExpandedItemIds }, { notes, force }) => {
    return { menuExpandedItemIds, notes, force };
  },
  target: afterFetchItemsInOpenendFoldersEvent,
});

$menuState.on(
  afterFetchItemsInOpenendFoldersEvent,
  (state, { menuExpandedItemIds, notes, force = false }: AfterFetchItemsInOpenendFoldersEventPayload) => {
    const menuState: MenuState = state[MenuType.WEBNOTES_MENU];
    const itemsMap: Record<string, NoteMenuItem> = {};

    // create map of fetched items
    notes.forEach((item: MenuItemWithOptionalDate) => {
      const isIconUrl = isValidUrl(item.icon as string);
      const updatedItem: NoteMenuItem = {
        type: MenuItemType.NOTE,
        id: item.id,
        parentId: item.parentId,
        hasChildren: false,
        icon: item.icon || 'note',
        name: item.name || 'New page',
        order: [],
        visible: true,
        targetId: '',
        index: 0,
        url: '',
        openInNewTab: false,
        slug: '',
        updatedAt: 'updatedAt' in item && typeof item.updatedAt === 'number' ? item.updatedAt : undefined,
        createdAt: 'createdAt' in item && typeof item.createdAt === 'number' ? item.createdAt : undefined,
        isShared: item.isShared,
      };

      if (isIconUrl) {
        updatedItem.iconUrl = item.icon;
      }

      itemsMap[item.id] = updatedItem;
    });

    // merge store entities with fetched items
    const entities = { ...menuState.entities, ...itemsMap };
    // exclude duplicates from ids
    const ids = [...new Set(Object.keys(entities))];
    // get all opened folders
    const folders = Object.values(menuState.entities).filter(
      ({ type, id }) => type === MenuItemType.NOTES_FOLDER && (force || menuExpandedItemIds.includes(id))
    );

    // set order for opened folders
    folders.forEach(({ id: parentId }) => {
      const order = entities[parentId]?.order || [];
      const fetchedItemsIds = Object.keys(itemsMap).filter((id) => itemsMap[id].parentId === parentId);

      const foldersOrder: string[] = [];
      order.forEach((id) => {
        if (entities[id].type === MenuItemType.NOTES_FOLDER) {
          foldersOrder.push(id);
        }
      });

      const sortedItemsInOrder = fetchedItemsIds
        .map((id) => itemsMap[id])
        .filter((item) => item)
        // TODO: get sort direction from store
        .sort(getSortFn<NoteMenuItem>(sortBy, sortOrder));

      // in case if have fetchedItemsIds, add them to order otherwise use order from store
      const newOrder = fetchedItemsIds.length
        ? [...new Set([...foldersOrder, ...sortedItemsInOrder.map((item) => item.id)])]
        : entities[parentId]?.order ?? [];

      // update order of opened folder
      entities[parentId] = {
        ...entities[parentId],
        order: newOrder,
      };

      // update index of parent items
      entities[parentId].order?.forEach((id, index) => {
        entities[id] = {
          ...entities[id],
          index,
        };
      });
    });

    return { ...state, [MenuType.WEBNOTES_MENU]: { ...menuState, entities, ids } };
  }
);

export const foldersToMenuTree = (params: GetNotesResult): MenuItemWithOptionalDate[] => {
  const { items } = params;
  const notesMap: Record<string, MenuItemWithOptionalDate> = {};
  notesMap[MenuItemType.NOTES_FOLDER + '#root'] = {
    id: `${MenuItemType.NOTES_FOLDER}#root`,
    positionType: 'sidebar',
    type: 'notesFolder',
    name: '',
    icon: '',
    parentId: '',
    index: 0,
    visible: true,
    url: '',
    slug: '',
    openInNewTab: false,
    hasChildren: true,
    targetId: '',
    children: [],
  };

  const mapItem = (item: NoteDto): MenuItemWithOptionalDate => {
    return {
      id: `${item.type === 'folder' ? MenuItemType.NOTES_FOLDER : MenuItemType.NOTE}#${item.globalId}`,
      positionType: 'sidebar',
      type: item.type === 'folder' ? 'notesFolder' : 'note',
      name: item.title,
      icon: item.emoji ?? '',
      parentId: `${MenuItemType.NOTES_FOLDER}#${item.parentId}`,
      index: 0,
      visible: true,
      url: '',
      slug: '',
      openInNewTab: false,
      hasChildren: item.type === 'folder',
      targetId: '',
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      isShared: item.shared,
      ...(item.type === 'folder' ? { children: [] } : {}),
    };
  };

  items.forEach((item) => {
    if (!item.isSharedForPortal) {
      const id = `${item.type === 'folder' ? MenuItemType.NOTES_FOLDER : MenuItemType.NOTE}#${item.globalId}`;
      notesMap[id] = mapItem(item);
    }
  });

  Object.values(notesMap).forEach((item) => {
    if (notesMap[item.id]) {
      notesMap[item.parentId]?.children?.push(notesMap[item.id]);
    }
  });

  const rootChildren = notesMap[MenuItemType.NOTES_FOLDER + '#root']?.children ?? [];

  sortChildren(rootChildren);

  return rootChildren;
};

const mapItem = (item: NoteDto): MenuItem => {
  return {
    id: `${MenuItemType.NOTE}#${item.globalId}`,
    type: MenuItemType.NOTE,
    name: item.title,
    icon: item.emoji ?? '',
    iconUrl: item.emoji ?? '',
    parentId: `${MenuItemType.NOTES_FOLDER}#${item.parentId}`,
    index: 0,
    visible: true,
    url: '',
    slug: '',
    openInNewTab: false,
    hasChildren: false,
    targetId: '',
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
};

export const notesToMenuTree = (
  items: NoteDto[]
): { map: { [key: string]: Record<string, MenuItem> }; parentIds: string[] } => {
  const parentIds: string[] = [];
  const map: { [key: string]: Record<string, MenuItem> } = {};

  items.forEach((item) => {
    const id = `${MenuItemType.NOTE}#${item.globalId}`;
    const parentId = `${MenuItemType.NOTES_FOLDER}#${item.parentId}`;

    if (!map[parentId]) {
      map[parentId] = {};
      parentIds.push(parentId);
    }
    map[parentId][id] = mapItem(item);
  });

  return { map, parentIds };
};

const notesToMenuStateEvent = createEvent<{
  notesState: NotesState;
  currentWorkspace: Workspace;
  notes: NoteDto[];
  type: NoteType;
  workspaceId: string;
}>();

/**
 * Set notes to menu tree after fetch all pages or a folder
 */
sample({
  source: {
    menuState: $menuState,
    notesState: $notes,
    currentWorkspace: $currentWorkspace,
  },
  clock: fetchNotesFx.doneData,
  fn: ({ menuState, notesState, currentWorkspace }, params) => {
    return { menuState, notesState, currentWorkspace, ...params };
  },
  target: notesToMenuStateEvent,
});

$menuState.on(notesToMenuStateEvent, (state, params) => {
  if (params.type !== 'note') return state;
  const notes: NoteDto[] = params.notes;

  const { map, parentIds } = notesToMenuTree(notes);

  const menu: MenuState = state[MenuType.WEBNOTES_MENU];
  let entities = { ...menu.entities };

  Object.keys(map).forEach((parentId) => {
    entities = { ...entities, ...map[parentId] };
  });

  parentIds.forEach((parentId) => {
    const order = entities[parentId]?.order || [];
    // fill order with correct sorting
    const newOrder = [...new Set([...order, ...Object.keys(map[parentId])])];

    const sortedItemsInOrder = newOrder
      .map((id) => entities[id])
      .filter((item) => item)
      // TODO: get sort direction from store
      .sort(getSortFn<MenuItem>(sortBy, sortOrder));

    entities[parentId] = {
      ...entities[parentId],
      order: [
        ...sortedItemsInOrder.filter((item) => item.type === MenuItemType.NOTES_FOLDER).map((item) => item.id),
        ...sortedItemsInOrder.filter((item) => item.type === MenuItemType.NOTE).map((item) => item.id),
      ],
    };
  });

  return {
    ...state,
    [MenuType.WEBNOTES_MENU]: {
      ...menu,
      entities: {
        ...entities,
      },
      ids: Object.keys(entities),
    },
  };
});

export interface ExpandAllParentFoldersEventParams {
  menuType: MenuType;
  /**
   * Currently selected item id, for example notesFolder#default
   */
  id: string;
  /**
   * ParentId of selected item, whose items need to be fetched
   */
  parentId: string | undefined;
  parentIds?: string[] | undefined;
  workspaceId?: string;
  /**
   * Do not scroll to item identified by id
   */
  doNotScrollToNote?: boolean;
}

export const expandAllParentFoldersEvent = createEvent<ExpandAllParentFoldersEventParams>();
export const prepareAllParentFoldersEvent = createEvent<ExpandAllParentFoldersEventParams>();

let abortController: AbortController | undefined;

export const fetchAllParentFoldersIntemsFx = createEffect(async (params: ExpandAllParentFoldersEventParams) => {
  const { workspaceId, parentIds, id } = params;

  if (abortController) {
    // cancel previous request
    abortController.abort();
  }

  // send request only if parentIds is not empty
  if (!parentIds?.filter((item) => item !== 'root').length || !workspaceId)
    return { id, notes: [], doNotScrollToNote: params.doNotScrollToNote };

  abortController = new AbortController();
  const result: { notes: NoteDto[] } = await getNotes({
    type: 'note',
    workspaceId,
    parentId: parentIds?.map((id) => id.split('#')[1]),
    abortController,
    fetchPortalsNotes: false,
    range: {},
  });

  return { id, notes: result.notes, doNotScrollToNote: params.doNotScrollToNote };
});

function sampleExpandAllParentFoldersFn(
  _: { menuState: MenusState; menuExpandedItemIds: string[] },
  params: ExpandAllParentFoldersEventParams
) {
  const { menuType, id, parentId } = params;
  const menu = _.menuState[menuType];
  const entities = { ...menu.entities };
  const parentIds: string[] = [];

  let item: MenuItem | undefined = entities[id ?? ''];

  if (!item && parentId) {
    item = entities[parentId];
    if (!item) return { ...params, parentIds };
    parentIds.push(parentId);
  }

  const findParent = (id: string, ids: string[]) => {
    const parentId = entities[id].parentId;
    if (parentId) {
      findParent(parentId, ids);
      if (!_.menuExpandedItemIds.includes(parentId)) {
        // add only not expanded folders
        ids.push(parentId);
      }
    }
  };

  item && findParent(item.id, parentIds);

  return { ...params, parentIds };
}

/**
 * 1. Expand all parent folders
 * 2. Fetch all items in parent folders
 * 3. Update menu state
 * 4. TODO: add loaded notes to $notes store
 */
sample({
  source: {
    menuState: $menuState,
    menuExpandedItemIds: $menuExpandedItemIds,
  },
  clock: expandAllParentFoldersEvent,
  greedy: false,
  fn: sampleExpandAllParentFoldersFn,
  target: [prepareAllParentFoldersEvent, fetchAllParentFoldersIntemsFx],
});

$menuExpandedItemIds.on(prepareAllParentFoldersEvent, (state, params) => {
  const { parentIds = [] } = params;

  const ids: string[] = [...state];
  parentIds?.forEach((id) => {
    if (!ids.includes(id)) {
      ids.push(id);
    }
  });

  // Update expanded items in local storage
  const expandedMenuItemIds = JSON.parse(localStorage.getItem(localStorageExpandedItemsKey) ?? '[]');
  expandedMenuItemIds.push(...parentIds.filter((id) => !expandedMenuItemIds.includes(id)));
  localStorage.setItem(localStorageExpandedItemsKey, JSON.stringify(expandedMenuItemIds));

  return ids;
});

$menuState.on(fetchAllParentFoldersIntemsFx.doneData, (state, params) => {
  const { map, parentIds } = notesToMenuTree(params.notes);
  const menu: MenuState = state[MenuType.WEBNOTES_MENU];
  let entities = { ...menu.entities };

  Object.keys(map).forEach((parentId) => {
    entities = { ...entities, ...map[parentId] };
  });

  parentIds.forEach((parentId) => {
    const folders = Object.values(menu.entities)
      .filter((item) => item.type === MenuItemType.NOTES_FOLDER && item.parentId === parentId)
      .map((item) => item.id);

    entities[parentId] = {
      ...entities[parentId],
      order: [...folders, ...Object.keys(map[parentId])],
    };
  });

  return {
    ...state,
    [MenuType.WEBNOTES_MENU]: {
      ...menu,
      entities: {
        ...entities,
      },
      ids: Object.keys(entities),
    },
  };
});

fetchAllParentFoldersIntemsFx.doneData.watch(({ id, doNotScrollToNote }) => {
  if (doNotScrollToNote) {
    return;
  }
  // using timeout coz, need to wait when menu tree will be updated
  setTimeout(() => {
    scrollToSidebarMenuItem(id);
  }, 0);
});

export const expandAllPortalParentFoldersEvent = createEvent<ExpandAllParentFoldersEventParams>();

sample({
  source: {
    menuState: $menuState,
    menuExpandedItemIds: $menuExpandedItemIds,
  },
  clock: expandAllPortalParentFoldersEvent,
  greedy: false,
  fn: sampleExpandAllParentFoldersFn,
  target: [prepareAllParentFoldersEvent],
});

export const updateMenuItemParentSortOrderEvent = createEvent<{ parentId: string; menuType: MenuType }>();

$menuState.on(updateMenuItemParentSortOrderEvent, (state, { parentId, menuType }) => {
  const menu: MenuState = state[menuType];
  const entities = { ...menu.entities };

  if (!entities[parentId]) return state;

  const order = entities[parentId]?.order || [];

  const items: MenuItem[] = [];
  const folders: MenuItem[] = [];
  const notes: MenuItem[] = [];

  order.forEach((id) => {
    if (entities[id]) {
      items.push(entities[id]);
    }
  });

  items.sort(getSortFn<MenuItem>(sortBy, sortOrder));

  items.forEach((item) => {
    if (item.type === MenuItemType.NOTES_FOLDER) {
      folders.push(item);
    } else {
      notes.push(item);
    }
  });

  const newOrder = [...folders, ...notes].map((item) => item.id);

  entities[parentId] = {
    ...entities[parentId],
    order: newOrder,
  };

  return {
    ...state,
    [menuType]: {
      ...menu,
      entities,
    },
  };
});
